import { useState, useEffect, useMemo, useRef } from "react";
import useIsMobile from "../client/hooks/useIsMobile";
import { startOfMonth } from "date-fns";
import { useParams } from "react-router-dom";
import {
  getActiveLayout,
  resetLayoutContent,
  getAccountsByUser,
  getAccountGroupsByUser,
  getTradingStrategiesByUser,
  useQuery,
  useAction,
} from "wasp/client/operations";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore — ensureDisciplineDefaultLayout will exist once `wasp start` regenerates the SDK
import { ensureDisciplineDefaultLayout } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { PaymentPlanId } from "../payment/plans";
import { Layers, ChevronDown, Loader2 } from "lucide-react";
import { Button } from "../client/components/ui/button";
import PlanSelectionModal from "../dashboard/PlanSelectionModal";
import { PageTransition } from "../client/components/ui/motion";
import { motion } from "framer-motion";
import { getTheme } from "../client/theme/themes";
import { useThemeId } from "../client/components/ui/card-section-header";
import useTranslation from "../client/hooks/useTranslation";
import { type DateRange } from "../client/components/DateRangePicker";
import { presetToDateRange } from "../client/utils/dateRange";
import { type AccountFilterValue } from "../trading-accounts/AccountGroupFilter";
import { WidgetDataProvider, type DashboardData } from "../dashboard/widgets/WidgetDataContext";
import DashboardLayout from "../dashboard/widgets/DashboardLayout";
import EditModeBar from "../dashboard/widgets/EditModeBar";
import AddWidgetPicker, { TOPBAR_WIDGET_KEYS } from "../dashboard/widgets/AddWidgetPicker";
import ResetLayoutDialog from "../dashboard/widgets/ResetLayoutDialog";
import LayoutLibraryModal from "../dashboard/widgets/LayoutLibraryModal";
import { SortableTopbarSlot, TopbarDndWrapper } from "../dashboard/widgets/TopbarSlotRenderer";
import TopbarFilterBar from "../dashboard/widgets/TopbarFilterBar";
import { useActiveLayout, compactSlots } from "../dashboard/widgets/useActiveLayout";
import { useLayoutsLibrary } from "../dashboard/widgets/useLayoutsLibrary";
import { getDefaultDisciplineLayout, withDefaultLayoutFallback } from "../dashboard/widgets/registry";
import type { LayoutSnapshot, SlotPlacement, WidgetKey } from "../dashboard/widgets/types";
import EdgyHeaderButton from "../edgy/EdgyHeaderButton";

export default function DisciplineManagerPage() {
  const t = useTranslation();
  const themeId = useThemeId();
  const isDark = useMemo(() => getTheme(themeId).isDark, [themeId]);
  const { data: user } = useAuth();
  const { noteId } = useParams<{ noteId: string }>();

  const isFreePlan = !user?.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free;
  const [showPlanModal, setShowPlanModal] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────
  // Filters
  // ─────────────────────────────────────────────────────────────────────────
  const [dateRange, setDateRange] = useState<DateRange>(() => presetToDateRange("all_time"));
  const [accountFilter, setAccountFilter] = useState<AccountFilterValue>({ type: "all" });
  const [strategyIds, setStrategyIds] = useState<string[]>([]);

  // ─────────────────────────────────────────────────────────────────────────
  // Reference data (for topbar pickers)
  // ─────────────────────────────────────────────────────────────────────────
  const { data: accountsDataInternal } = useQuery(getAccountsByUser);
  const { data: groupsInternal = [] } = useQuery(getAccountGroupsByUser);
  const { data: strategiesInternal = [] } = useQuery(getTradingStrategiesByUser);
  const accounts = ((accountsDataInternal as any)?.accounts ?? []) as any[];
  const groups = (groupsInternal ?? []) as any[];
  const strategies = (strategiesInternal ?? []) as any[];

  // ─────────────────────────────────────────────────────────────────────────
  // Layout system (scope "discipline")
  // ─────────────────────────────────────────────────────────────────────────
  const { data: serverLayout, refetch: refetchLayout } = useQuery(getActiveLayout, { scope: "discipline" });

  const ensureFn = useAction(ensureDisciplineDefaultLayout);
  const ensureCalledRef = useRef(false);
  useEffect(() => {
    if (ensureCalledRef.current) return;
    ensureCalledRef.current = true;
    ensureFn(undefined).then(() => refetchLayout()).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initialLayout: LayoutSnapshot = useMemo(() => {
    if (!serverLayout) return getDefaultDisciplineLayout();
    const rows = [...serverLayout.rows].sort((a, b) => a.order - b.order);
    const allSlots: SlotPlacement[] = [];

    const isNewStyle = rows.length === 0 || rows.every((r) => r.cols === 6);

    if (isNewStyle) {
      for (const row of rows) {
        for (const s of row.slots) {
          allSlots.push({
            id: s.id,
            widgetKey: s.widgetKey as WidgetKey,
            colStart: s.colStart,
            rowStart: row.order,
            colSpan: s.colSpan,
            rowSpan: s.rowSpan,
            ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
            ...((s as any).placement ? { placement: (s as any).placement } : {}),
            ...((s as any).defaultFilterValue != null ? { defaultFilterValue: (s as any).defaultFilterValue } : {}),
            ...((s as any).viewMode != null ? { viewMode: (s as any).viewMode } : {}),
          });
        }
      }
    } else {
      let cursor = 1;
      for (const row of rows) {
        const maxRowSpan = row.slots.reduce((m: number, s: any) => Math.max(m, s.rowSpan ?? 1), 1);
        for (const s of row.slots) {
          allSlots.push({
            id: s.id,
            widgetKey: s.widgetKey as WidgetKey,
            colStart: s.colStart,
            rowStart: cursor,
            colSpan: s.colSpan,
            rowSpan: s.rowSpan,
            ...(s.notebookNoteId ? { notebookNoteId: s.notebookNoteId } : {}),
          });
        }
        cursor += maxRowSpan;
      }
    }

    const topbar = allSlots.filter((s) => s.placement === "topbar");
    const grid = allSlots.filter((s) => s.placement !== "topbar");
    return withDefaultLayoutFallback({ slots: [...topbar, ...compactSlots(grid)] }, "discipline");
  }, [serverLayout]);

  const {
    layout,
    addWidget,
    removeWidget,
    setWidgetSize,
    moveWidget,
    swapWidgets,
    flushSave,
    compactAndFlushSave,
    updateSlotDefaultFilterValue,
    updateSlotViewMode,
  } = useActiveLayout(initialLayout, (serverLayout as any)?.id ?? null, undefined, false);

  const topbarSlots = useMemo(
    () => layout.slots.filter((s) => s.placement === "topbar").sort((a, b) => a.colStart - b.colStart),
    [layout.slots],
  );

  // ─────────────────────────────────────────────────────────────────────────
  // Edit-mode state
  // ─────────────────────────────────────────────────────────────────────────
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const isDuplicatingRef = useRef(false);
  const [layoutLibraryOpen, setLayoutLibraryOpen] = useState(false);
  const { activeLayout: libraryActiveLayout, duplicate, activate } = useLayoutsLibrary("discipline");
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [pickerContext, setPickerContext] = useState<{
    colStart: number;
    rowStart: number;
    maxColSpan: number;
    context?: "topbar" | "grid";
  } | null>(null);
  const resetLayoutFn = useAction(resetLayoutContent);
  const isMobile = useIsMobile();

  // ─────────────────────────────────────────────────────────────────────────
  // Widget context data (neutral defaults — discipline widgets self-fetch)
  // ─────────────────────────────────────────────────────────────────────────
  const widgetData = {
    // — neutral trading data —
    trades: [],
    routines: [],
    goals: [],
    accounts,
    allAccounts: accounts,
    groups,
    strategies,
    filteredAccounts: accounts,
    sessions: [],
    metrics: null,
    statsTrades: [],
    statsTradesList: [],
    equityCurveData: { points: [], totalPnl: 0, xMin: 0, xMax: 0 },
    accountEvents: [],
    fundedEvents: [],
    payoutEvents: [],
    violationsByDate: new Map(),
    pendingRoutines: [],
    topGoals: [],
    statsPeriod: "30d" as const,
    // — filters —
    accountFilter,
    setAccountFilter,
    strategyIds,
    setStrategyIds,
    dateRange,
    setDateRange,
    statsStartDate: dateRange.from ?? null,
    statsEndDate: dateRange.to ?? null,
    setSelectedDay: () => {},
    handleCompleteRoutine: () => {},
    setSelectedMonth: () => {},
    onAddTrade: () => {},
    onPromoteToFunded: () => {},
    onOpenPlanModal: () => setShowPlanModal(true),
    // — flags —
    isDark,
    themeId,
    isFreePlan,
    readOnly: false,
    hasMentor: false,
    t,
    chartMode: "networth" as const,
    setChartMode: () => {},
    // — PFM neutral defaults —
    charges: [],
    tptWalletContext: null,
    calendarEvents: [],
    pfmTemplateFilter: null,
    setPfmTemplateFilter: () => {},
    accountStatusFilter: null,
    setAccountStatusFilter: () => {},
    calendarMonth: startOfMonth(new Date()),
    setCalendarMonth: () => {},
    showImportModal: false,
    setShowImportModal: () => {},
    showAddPayoutModal: false,
    setShowAddPayoutModal: () => {},
    isLoadingPfm: false,
    userId: user?.id,
    hiddenAccountsCount: 0,
    showPlanModal,
    setShowPlanModal,
    // — discipline-specific —
    notebookInitialNoteId: noteId ?? undefined,
  } as unknown as DashboardData;

  const extraTopbarActions = (
    <>
      {/* Edgy sits with the page's other toolbar actions. */}
      <EdgyHeaderButton />
      <Button
        variant="outline"
        size="sm"
        className="text-xs h-8 gap-1 max-w-[140px]"
        onClick={() => setLayoutLibraryOpen(true)}
      >
        <Layers className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">{(libraryActiveLayout as any)?.name ?? t.dashboard.layouts.menuTitle}</span>
        <ChevronDown className="h-3 w-3 shrink-0" />
      </Button>
      {!isEditMode && (
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-8 min-w-[5rem]"
          disabled={isDuplicating}
          onClick={async () => {
            if (isFreePlan) {
              setShowPlanModal(true);
              return;
            }
            if ((libraryActiveLayout as any)?.isReadOnly) {
              if (isDuplicatingRef.current) return;
              isDuplicatingRef.current = true;
              setIsDuplicating(true);
              let success = false;
              try {
                const created = await duplicate((libraryActiveLayout as any).id, t.dashboard.layouts.newLayout);
                if (created) {
                  const activated = await activate((created as any).id);
                  if (activated) success = true;
                }
              } finally {
                setIsDuplicating(false);
                isDuplicatingRef.current = false;
              }
              if (!success) return;
              await refetchLayout();
            }
            setIsEditMode(true);
          }}
        >
          {isDuplicating ? (
            <><Loader2 className="h-3 w-3 animate-spin" />{t.dashboard.customize}</>
          ) : (
            t.dashboard.customize
          )}
        </Button>
      )}
    </>
  );

  const renderTopbar = () => {
    if (isEditMode) {
      return (
        <TopbarDndWrapper
          slots={topbarSlots}
          onSwap={(idA, idB) => { swapWidgets(idA, idB); flushSave(); }}
        >
          {topbarSlots.map((slot) => (
            <SortableTopbarSlot
              key={slot.id}
              id={slot.id}
              slot={slot}
              isEditMode={true}
              dateRange={dateRange}
              accountFilter={accountFilter}
              strategyIds={strategyIds}
              accounts={accounts}
              groups={groups}
              strategies={strategies}
              onDateRangeChange={setDateRange}
              onAccountFilterChange={setAccountFilter}
              onStrategyIdsChange={setStrategyIds}
              onAddTrade={() => {}}
              onAddPayout={() => {}}
              onRemove={() => { removeWidget(slot.id); flushSave(); }}
              onPin={(value) => {
                updateSlotDefaultFilterValue(slot.id, value);
                flushSave();
              }}
            />
          ))}
        </TopbarDndWrapper>
      );
    }
    return (
      <TopbarFilterBar
        slots={topbarSlots}
        dateRange={dateRange}
        accountFilter={accountFilter}
        strategyIds={strategyIds}
        accounts={accounts}
        groups={groups}
        strategies={strategies}
        onDateRangeChange={setDateRange}
        onAccountFilterChange={setAccountFilter}
        onStrategyIdsChange={setStrategyIds}
        onAddTrade={() => {}}
        onAddPayout={() => {}}
        onRemove={() => {}}
        onPin={undefined}
        extraActions={extraTopbarActions}
      />
    );
  };

  return (
    <PageTransition className="w-full">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="sticky top-0 z-30 -mx-3 sm:-mx-5 mb-5 flex items-center justify-between gap-4 min-h-10 border-b border-border bg-background/80 px-3 sm:px-5 py-3 backdrop-blur-md"
      >
        <h1 className="flex items-center gap-2.5 text-lg font-semibold shrink-0">
          {t.disciplineManager.title}
        </h1>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {renderTopbar()}
          {isEditMode && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-8"
              onClick={() => setPickerContext({ colStart: 1, rowStart: 1, maxColSpan: 6, context: "topbar" })}
            >
              {t.pages.topbarAdd}
            </Button>
          )}
          <LayoutLibraryModal
            open={layoutLibraryOpen}
            onOpenChange={setLayoutLibraryOpen}
            scope="discipline"
            isFreePlan={isFreePlan}
            onUpgradeClick={() => setShowPlanModal(true)}
            activeLayoutId={(libraryActiveLayout as any)?.id ?? null}
            onNewLayoutActivated={async () => {
              await refetchLayout();
              setIsEditMode(true);
            }}
            onBeforeActivate={async () => {
              await flushSave();
            }}
          />
        </div>
      </motion.div>

      {isEditMode && (
        <EditModeBar
          onReset={() => setShowResetDialog(true)}
          onDone={async () => { await compactAndFlushSave(); setIsEditMode(false); }}
        />
      )}

      <WidgetDataProvider value={widgetData}>
        <DashboardLayout
          layout={layout}
          isEditMode={isEditMode}
          isMobile={isMobile}
          onAddWidgetClick={(colStart, rowStart, maxColSpan) =>
            setPickerContext({ colStart, rowStart, maxColSpan })
          }
          onRemoveWidget={removeWidget}
          onWidgetSizeChange={setWidgetSize}
          onMoveWidget={moveWidget}
          onSwapWidgets={swapWidgets}
          onUpdateSlotConfig={updateSlotDefaultFilterValue}
          onUpdateSlotViewMode={updateSlotViewMode}
        />
      </WidgetDataProvider>

      <AddWidgetPicker
        open={pickerContext !== null}
        onOpenChange={(open) => { if (!open) setPickerContext(null); }}
        layout={layout}
        maxColSpan={pickerContext?.maxColSpan ?? 1}
        rowStart={pickerContext?.rowStart ?? 1}
        colStart={pickerContext?.colStart ?? 1}
        scope="discipline"
        initialCategory={pickerContext?.context === "topbar" ? "topbar" : undefined}
        isFreePlan={isFreePlan}
        onUpgradeClick={() => setShowPlanModal(true)}
        onPick={(widgetKey) => {
          if (!pickerContext) return;
          if (TOPBAR_WIDGET_KEYS.has(widgetKey)) {
            addWidget(widgetKey, 1, 0, 1, 1, "topbar");
          } else {
            const maxOccupiedRow = layout.slots
              .filter((s) => s.placement !== "topbar")
              .reduce((m, s) => Math.max(m, s.rowStart + s.rowSpan - 1), 0);
            addWidget(widgetKey, pickerContext.colStart, maxOccupiedRow + 1, pickerContext.maxColSpan);
          }
          setPickerContext(null);
        }}
      />

      <ResetLayoutDialog
        open={showResetDialog}
        onOpenChange={setShowResetDialog}
        onConfirm={async () => {
          const layoutId = (serverLayout as any)?.id as string | undefined;
          if (layoutId) {
            await resetLayoutFn({ layoutId });
            await refetchLayout();
          }
          setIsEditMode(false);
        }}
      />

      <PlanSelectionModal open={showPlanModal} onOpenChange={setShowPlanModal} />
    </PageTransition>
  );
}
